import { Check, Building2, FileText, MapPin } from "lucide-react";
import type { CompanyValues, TemplateValues, SiteValues } from "./schemas";

interface Props {
  company: CompanyValues;
  template: TemplateValues;
  site: SiteValues;
}

const STANDARD_LABELS: Record<string, string> = {
  "e1527-21": "ASTM E1527-21 (Phase I ESA)",
  "e1903-19": "ASTM E1903-19 (Phase II ESA)",
  aai: "EPA All Appropriate Inquiries",
};

function Row({
  icon,
  title,
  items,
}: {
  icon: React.ReactNode;
  title: string;
  items: { label: string; value: string }[];
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/50 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
          {icon}
        </div>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <dl className="grid gap-1.5 text-sm">
        {items.map((it) => (
          <div key={it.label} className="grid grid-cols-[120px_1fr] gap-2">
            <dt className="text-muted-foreground">{it.label}</dt>
            <dd className="font-medium truncate">{it.value || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function StepRecap({ company, template, site }: Props) {
  const hasSite = !!(site.address && site.city && site.state);
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-3">
        <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium text-success">Ready to finish</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Review the values below — clicking <strong>Finish</strong> saves
            them and pre-fills your dashboard and report wizard.
          </p>
        </div>
      </div>

      <Row
        icon={<Building2 className="h-3.5 w-3.5" />}
        title="Company profile"
        items={[
          { label: "Name", value: company.displayName },
          { label: "Company", value: company.company ?? "" },
          { label: "PE license", value: company.peLicense ?? "" },
          { label: "Office", value: company.office ?? "" },
        ]}
      />

      <Row
        icon={<FileText className="h-3.5 w-3.5" />}
        title="Default report template"
        items={[
          {
            label: "Standard",
            value: STANDARD_LABELS[template.standard] ?? template.standard,
          },
          { label: "Jurisdiction", value: template.jurisdictionState ?? "" },
          {
            label: "Boilerplate",
            value: (template.additionalRequirements ?? "").slice(0, 80) +
              ((template.additionalRequirements ?? "").length > 80 ? "…" : ""),
          },
        ]}
      />

      <Row
        icon={<MapPin className="h-3.5 w-3.5" />}
        title={hasSite ? "First site (will create a draft report)" : "First site"}
        items={
          hasSite
            ? [
                { label: "Address", value: site.address ?? "" },
                {
                  label: "City / State",
                  value: `${site.city ?? ""}${site.state ? `, ${site.state}` : ""}`,
                },
                { label: "ZIP", value: site.zip ?? "" },
              ]
            : [{ label: "Status", value: "Skipped — add later from /sites" }]
        }
      />
    </div>
  );
}
